/** Scroll position of the scrollable element. */
export type ScrollPosition = {
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
};

/** State tracked to hide an element while scrolling down. */
export type HideOnScrollState = {
  /** Whether the element is hidden. */
  isHidden: boolean;
  /** Scroll position of the last visibility decision (reference for the next scroll delta). */
  lastScrollTop: number;
};

/**
 * Computes whether an element (a header…) must be hidden after a scroll: hidden while scrolling
 * down, shown back as soon as the user scrolls up or goes back to the top.
 * Moves smaller than the threshold are accumulated, so slow scrolls still count but small
 * jitters do not toggle the element.
 *
 * @param position - Current scroll position of the scrollable element.
 * @param state - State after the previous scroll.
 * @param threshold - Minimum scroll distance (in px) before the visibility changes.
 * @returns The new state (the same object when nothing changes).
 */
export const getNextHideOnScrollState = (
  { scrollTop, clientHeight, scrollHeight }: ScrollPosition,
  state: HideOnScrollState,
  threshold: number,
): HideOnScrollState => {
  const delta = scrollTop - state.lastScrollTop;

  // Hiding the element enlarges the scrollable area, which makes the browser clamp scrollTop
  // when at the bottom: ignore this upward move so the element does not flicker.
  const isAtBottom = scrollTop + clientHeight >= scrollHeight - threshold;

  if (isAtBottom && delta < 0) {
    return { ...state, lastScrollTop: scrollTop };
  }

  if (Math.abs(delta) < threshold) {
    return state;
  }

  return {
    isHidden: delta > 0 && scrollTop > threshold,
    lastScrollTop: scrollTop,
  };
};
